import { useState } from 'react';
import { Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { T, stitchTooltipSlotProps } from '../../ProjectOverview/parts/stitch';
import { faseSegmenten } from './fasen';
import { R } from './stijl';

/**
 * De fasebalk als één dunne lijn: per fase een segment van 4px met de naam eronder. Alleen
 * de huidige fase staat in de accentkleur; afgeronde fasen in een zachte tint, de rest
 * neutraal. Klik op een fasenaam (of het segment) = keuzemenu: werkvlak van die fase
 * openen, "Dit is de huidige fase" (Fase wijzigen), terug naar automatisch, fasen aanpassen.
 */
export default function FaseLijn({ phases, bezig = false, onWerkvlak, onMaakHuidig, onAutomatisch, onFasenAanpassen }) {
  const segmenten = faseSegmenten(phases);
  const [menu, setMenu] = useState(null); // { anker, seg }
  if (!segmenten.length) return null;
  const kolommen = `repeat(${segmenten.length}, minmax(0, 1fr))`;

  return (
    <Box component="nav" aria-label="Fasen" sx={{ my: { xs: 3, md: 4.5 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: kolommen, columnGap: '6px' }}>
        {segmenten.map((s) => (
          <Tooltip key={s.id} title={`${s.uitleg}. Klik om de fase te wijzigen of het werkvlak te openen.`}
            slotProps={stitchTooltipSlotProps} describeChild>
            <Box component="button" type="button" aria-label={s.label}
              aria-current={s.stand === 'nu' ? 'step' : undefined}
              aria-haspopup="menu"
              onClick={(e) => setMenu({ anker: e.currentTarget, seg: s })}
              sx={{
                border: 0, bgcolor: 'transparent', p: 0, pb: 0.5, cursor: 'pointer', textAlign: 'left', minWidth: 0,
                '&:hover .fase-naam': { color: R.accent },
                '&:focus-visible': { outline: `2px solid ${R.accent}`, outlineOffset: 2, borderRadius: '4px' },
              }}>
              <Box sx={{ height: 4, borderRadius: '2px',
                bgcolor: s.stand === 'nu' ? R.accent : s.stand === 'klaar' ? R.accentZacht : R.lijn }} />
              <Typography component="span" className="fase-naam" sx={{
                display: 'block', mt: 1, fontFamily: T.fontBody, fontSize: '0.88rem', lineHeight: 1.3,
                color: s.stand === 'nu' ? R.accent : R.stil, fontWeight: s.stand === 'nu' ? 600 : 400,
                whiteSpace: 'nowrap',
                // De huidige fase mag over de buren heen lopen (smal scherm); de rest kapt netjes af.
                ...(s.stand === 'nu' ? { overflow: 'visible' } : { overflow: 'hidden', textOverflow: 'ellipsis' }),
                // Smal scherm: alleen de huidige fase benoemd (de rest blijft klikbaar via het segment).
                visibility: { xs: s.stand === 'nu' ? 'visible' : 'hidden', md: 'visible' },
              }}>
                {s.label}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      <Menu anchorEl={menu?.anker} open={!!menu} onClose={() => setMenu(null)}
        slotProps={{ paper: { sx: { borderRadius: '0.75rem', minWidth: 260, maxWidth: 340 } } }}>
        {menu && (
          <Box sx={{ px: 2, pt: 1, pb: 1.25 }}>
            <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.95rem', color: T.text }}>{menu.seg.label}</Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: R.stil, mt: 0.25 }}>
              {menu.seg.stand === 'nu' ? 'Huidige fase' : menu.seg.stand === 'klaar' ? 'Afgerond' : 'Nog niet de huidige fase'}
            </Typography>
          </Box>
        )}
        {menu && (
          <MenuItem onClick={() => { const k = menu.seg.phase_key; setMenu(null); onWerkvlak(k); }} sx={itemSx}>
            Werkvlak van {menu.seg.label} openen
          </MenuItem>
        )}
        {menu && menu.seg.stand !== 'nu' && (
          <MenuItem disabled={bezig} onClick={() => { const k = menu.seg.phase_key; setMenu(null); onMaakHuidig(k); }} sx={itemSx}>
            Fase wijzigen: dit is de huidige fase
          </MenuItem>
        )}
        {menu && menu.seg.override && (
          <MenuItem disabled={bezig} onClick={() => { const seg = menu.seg; setMenu(null); onAutomatisch(seg); }} sx={itemSx}>
            Status weer automatisch laten bepalen
          </MenuItem>
        )}
        {menu && (
          <MenuItem disabled={bezig} onClick={() => { setMenu(null); onFasenAanpassen(); }} sx={{ ...itemSx, color: R.tekst2 }}>
            Fasen aanpassen…
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

const itemSx = { fontFamily: T.fontBody, fontSize: '0.88rem', color: T.text, py: 1 };
